import React, { useCallback, useEffect, useState } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import { Row, Col, Button, Badge } from "reactstrap";
import { Block, Icon } from "../../../components/Component";
import { nanoid } from "nanoid";
import { Table, Tag, Space, Tooltip } from "antd";
import type { TableColumnsType } from "antd";
import WorkOrderLineModal from "./WorkOrderLineModal";
import Swal from "sweetalert2";

// Enhanced interface for allocated materials
interface AllocatedMaterial {
  id: string;
  materialId: string;
  materialName: string;
  quantity: number;
  uom: string;
  category?: string;
  notes?: string;
  boqLineId?: string;
  boqTargetLineId?: string;
}

interface WorkOrderLine {
  id: string;
  workCategory: string;
  workSubCategory?: string;
  taskTitle: string;
  taskDescription: string;
  quantity: number;
  uom: string;
  rate?: number; // Rate per unit
  allocatedMaterials: AllocatedMaterial[]; // Updated to use enhanced structure
  towerVilla?: string;
  towerVillaName?: string;
  floorName?: string;
  flatName?: string;
  uploadedDocs: any[];
  errorFlag?: boolean;
  errorMessage?: string[];
  // Budget line fields
  budgetLineId?: number;
  budgetLineName?: string;
  allocatedAmount?: number;
  budgetValue?: number;
}

const WorkBoqOrderLines: React.FC = (props: any) => {
  const [generalDetails, setGeneralDetails] = useState<any>();

  // Budget allocation tracking
  const [budgetAllocations, setBudgetAllocations] = useState<{
    [key: number]: number;
  }>({});
  const [activeWorkLine, setActiveWorkLine] = useState<any>({
    action: null,
    index: null,
    item: null,
  });
  const [sm, setSm] = useState(false);
  const [screenSize, setScreenSize] = useState(0);
  const [mobileView, setMobileView] = useState(false);

  const { control, reset, setValue, watch, handleSubmit } = useFormContext();

  const previousFormData = watch();
  const workLevelWatch = watch("workLevel");
  const siteIdWatch = watch("siteId");

  const { fields, append, remove, update }: any = useFieldArray({
    control,
    name: "workOrderLines",
  });

  // Helper function to render material allocation summary
  const renderMaterialsSummary = (materials: AllocatedMaterial[]) => {
    if (!materials || materials.length === 0) {
      return (
        <Space>
          <span>0 items</span>
          <Icon name="cross-circle" className="text-danger" />
        </Space>
      );
    }

    const totalQuantity = materials.reduce(
      (sum, material) => sum + material.quantity,
      0
    );

    return (
      <Tooltip
        // title={
        //   <div>
        //     <div className="mb-2">
        //       <strong>Materials Summary:</strong>
        //     </div>
        //     {materials.map((material, idx) => (
        //       <div key={idx} className="mb-1">
        //         • {material.materialName}: {material.quantity} {material.uom}
        //         {material.category && (
        //           <Tag color="blue" style={{ marginLeft: 4 }}>
        //             {material.category}
        //           </Tag>
        //         )}
        //       </div>
        //     ))}
        //     <div
        //       className="mt-2 pt-2"
        //       style={{ borderTop: "1px solid rgba(255,255,255,0.2)" }}
        //     >
        //       <strong>
        //         Total: {materials.length} items, {totalQuantity.toFixed(2)}{" "}
        //         units
        //       </strong>
        //     </div>
        //   </div>
        // }
        placement="left"
        overlayStyle={{ maxWidth: 400 }}
      >
        <Space>
          <span>{materials.length} items</span>
          <Icon name="check-circle" className="text-success" />
          {/* <Badge color="primary" pill>
            {totalQuantity.toFixed(1)}
          </Badge> */}
        </Space>
      </Tooltip>
    );
  };

  // Helper function to render detailed materials in expandable row
  const renderDetailedMaterials = (materials: AllocatedMaterial[]) => {
    if (!materials || materials.length === 0) {
      return (
        <div className="text-muted">
          <Icon name="info" className="me-2" />
          No materials allocated
        </div>
      );
    }

    return (
      <div>
        <strong>Allocated Materials ({materials.length}):</strong>
        <div className="mt-2">
          <Table
            className="customTable"
            columns={[
              {
                title: "Material",
                key: "material",
                width: "40%",
                render: (_, record: AllocatedMaterial) => (
                  <div>
                    <div className="fw-medium">{record.materialName}</div>
                    {record.category && (
                      <Tag color="blue">{record.category}</Tag>
                    )}
                  </div>
                ),
              },
              {
                title: "Quantity",
                key: "quantity",
                width: "20%",
                render: (_, record: AllocatedMaterial) => (
                  <span>
                    {record.quantity} {(record as any).uomName || record.uom}
                  </span>
                ),
              },
              {
                title: "UOM",
                dataIndex: "uom",
                key: "uom",
                width: "15%",
                render: (uom: string, record: AllocatedMaterial) => (
                  <Tag color="geekblue">{(record as any).uomName || uom}</Tag>
                ),
              },
            ]}
            dataSource={materials}
            rowKey="id"
            pagination={false}
            size="small"
            bordered
          />

          {/* Summary row */}
          {/* <div className="mt-2 p-2 bg-light rounded">
            <Space split={<span className="text-muted">|</span>}>
              <span>
                <strong>Total Materials:</strong> {materials.length}
              </span>
              <span>
                <strong>Total Quantity:</strong>{" "}
                {materials.reduce((sum, m) => sum + m.quantity, 0).toFixed(2)}{" "}
                units
              </span>
              <span>
                <strong>Categories:</strong>{" "}
                {new Set(materials.map((m) => m.category).filter(Boolean)).size}
              </span>
            </Space>
          </div> */}
        </div>
      </div>
    );
  };

  const [columns, setColumns] = useState<TableColumnsType<any>>([
    {
      title: "#",
      key: "#",
      width: 50,
      fixed: "left",
      render: (text, record, index) => index + 1,
    },
    {
      title: "Work Category",
      dataIndex: "workCategory",
      key: "workCategory",
      width: 150,
      fixed: "left",
      render: (text, record) => (
        <div className="d-flex align-items-center justify-content-between pe-2">
          <span>{record.workCategoryName || record.workCategory}</span>
          {record?.errorFlag == true && (
            <Badge color="outline-danger" style={{ cursor: "pointer" }}>
              <span>Error</span>
              <Icon name="info" />
            </Badge>
          )}
        </div>
      ),
    },
    // {
    //   title: "Sub Category",
    //   dataIndex: "workSubCategory",
    //   key: "workSubCategory",
    //   width: 130,
    //   render: (text, record) => record.workSubCategoryName || text || "-",
    // },
    {
      title: "Task Title",
      dataIndex: "taskTitle",
      key: "taskTitle",
      width: 200,
    },
    {
      title: "Quantity",
      dataIndex: "quantity",
      key: "quantity",
      width: 120,
      render: (text, record) =>
        `${Number(text).toFixed(2)} ${record?.uomName || record?.uom}`,
    },
    {
      title: "Rate",
      dataIndex: "rate",
      key: "rate",
      width: 120,
      render: (text, record) => {
        const rate = record.rate || 0;
        return `₹${Number(rate).toLocaleString()}`;
      },
    },
    {
      title: "Line Total",
      key: "lineTotal",
      width: 140,
      render: (text, record) => {
        const quantity = record.quantity || 0;
        const rate = record.rate || 0;
        const lineTotal = quantity * rate;
        return <strong>₹{Number(lineTotal).toLocaleString()}</strong>;
      },
    },
    {
      title: "Location",
      key: "location",
      width: 180,
      hidden: !["Tower / Block", "Villa", "Floor", "Flat"].includes(
        workLevelWatch
      ),
      render: (text, record) => {
        // For Villa level, only show flatName as villa number
        if (workLevelWatch === "Villa") {
          return record.flatNameDisplay || record.towerVillaName || "-";
        }

        // For Tower/Block level
        if (workLevelWatch === "Tower / Block") {
          return record.towerVillaName || record.towerVilla || "-";
        }
        // For Floor level
        if (workLevelWatch === "Floor") {
          const parts = [];
          if (record.towerVillaName) parts.push(record.towerVillaName);
          if (record.floorName) parts.push(`Floor ${record.floorName}`);
          return parts.join(" / ") || "-";
        }

        // For Flat level - show full hierarchy
        if (workLevelWatch === "Flat") {
          const parts = [];
          if (record.towerVillaName) parts.push(record.towerVillaName);
          if (record.floorName) parts.push(`Floor ${record.floorName}`);
          if (record.flatNameDisplay) parts.push(record.flatNameDisplay);
          return parts.join(" / ") || "-";
        }
        // For other levels, build location path
        const locationParts = [];
        if (record.towerVilla)
          locationParts.push(record.towerVillaName || record.towerVilla);
        if (record.floorName) locationParts.push(record.floorName);
        if (record.flatName)
          locationParts.push(record.flatNameDisplay || record.flatName);
        return locationParts.join(" / ") || "-";
      },
    },
    {
      title: "Materials",
      key: "materials",
      width: 140,
      render: (text, record) =>
        renderMaterialsSummary(record?.allocatedMaterials || []),
    },
    {
      title: "Budget",
      key: "budget",
      width: 150,
      render: (text, record) => {
        if (record.budgetLineId) {
          return (
            <div>
              <div className="small fw-bold">{record.budgetLineName}</div>
              <div className="small text-muted">
                ₹{(record.allocatedAmount || 0).toLocaleString()}
              </div>
            </div>
          );
        }
        return <span className="text-muted">No budget allocated</span>;
      },
    },
    {
      title: "Docs",
      key: "docs",
      width: 80,
      render: (text, record) =>
        record?.uploadedDocs?.length > 0 ? (
          <Tooltip title={`${record.uploadedDocs.length} documents uploaded`}>
            <Space>
              <Icon name="check-circle" className="text-success" />
              <Badge color="success" pill>
                {record.uploadedDocs.length}
              </Badge>
            </Space>
          </Tooltip>
        ) : (
          <Tooltip title="No documents uploaded">
            <Icon name="cross-circle" className="text-danger" />
          </Tooltip>
        ),
    },
    {
      title: "Action",
      key: "operation",
      fixed: "right",
      width: 120,
      render: (text, record, index) => (
        <Space>
          <Tooltip title="Edit">
            <Button
              size="sm"
              color=""
              onClick={() => {
                // Restore budget allocation for edit mode
                handleEditModeEntry(record);
                setActiveWorkLine({
                  action: "edit",
                  index: index,
                  item: record,
                });
              }}
            >
              <Icon name="edit" />
            </Button>
          </Tooltip>
          <Tooltip title="Delete">
            <Button
              size="sm"
              color=""
              onClick={() => {
                Swal.fire({
                  title: "Delete Work Order Line",
                  text: "Are you sure you want to delete this work order line?",
                  icon: "warning",
                  showCancelButton: true,
                  confirmButtonColor: "#d33",
                  cancelButtonColor: "#3085d6",
                  confirmButtonText: "Yes, delete it!",
                }).then((result) => {
                  if (result.isConfirmed) {
                    remove(index);
                    Swal.fire(
                      "Deleted!",
                      "Work order line has been deleted.",
                      "success"
                    );
                  }
                });
              }}
            >
              <Icon name="trash" />
            </Button>
          </Tooltip>
          <Tooltip title="Duplicate">
            <Button
              size="sm"
              color=""
              onClick={() => {
                setActiveWorkLine({
                  action: "add",
                  index: null,
                  item: {
                    ...record,
                    id: nanoid(),
                    taskTitle: `${record.taskTitle} (Copy)`, // Modify title to avoid duplicate
                  },
                });
              }}
            >
              <Icon name="copy" />
            </Button>
          </Tooltip>
        </Space>
      ),
    },
  ]);

  const updateColumnsWithWorkLevel = useCallback(() => {
    setColumns((prevColumns) => {
      return prevColumns.map((col: any) => {
        if (col.key === "location") {
          const showLocation = ["Tower / Block", "Villa", "Floor", "Flat"].includes(
            workLevelWatch
          ) || ["Tower", "Block"].includes(workLevelWatch);

          return {
            ...col,
            hidden: !showLocation,
          };
        }
        return col;
      });
    });
  }, [workLevelWatch]);

  useEffect(() => {
    updateColumnsWithWorkLevel();
  }, [workLevelWatch, updateColumnsWithWorkLevel]);

  const addNewRow = () => {
    setActiveWorkLine({
      action: "add",
      index: null,
      item: null,
    });
  };

  const viewChange = () => {
    setScreenSize(window.innerWidth);
    if (window.innerWidth < 990) {
      setMobileView(true);
    } else {
      setMobileView(false);
      setSm(false);
    }
  };

  useEffect(() => {
    viewChange();
    window.addEventListener("load", viewChange);
    window.addEventListener("resize", viewChange);
    const headerClick = () => setSm(false);
    document
      .getElementsByClassName("nk-header")[0]
      ?.addEventListener("click", headerClick);

    return () => {
      window.removeEventListener("resize", viewChange);
      window.removeEventListener("load", viewChange);
      document
        .getElementsByClassName("nk-header")[0]
        ?.removeEventListener("click", headerClick);
    };
  }, []);

  // Block duplicates by task title + site hierarchy (Tower/Floor/Flat).
  // The separate `locationId` field is intentionally ignored.
  const validateDuplicateTaskAtHierarchy = (
    data: any,
    excludeIndex?: number
  ) => {
    const newTitle = (data.taskTitle || "").toLowerCase().trim();
    const newTower = data.towerVilla ?? "";
    const newFloor = data.floorName ?? "";
    const newFlat = data.flatName ?? "";

    const duplicate = fields.find((line: any, index: number) => {
      if (index === excludeIndex) return false;
      if ((line.taskTitle || "").toLowerCase().trim() !== newTitle) return false;
      return (
        (line.towerVilla ?? "") === newTower &&
        (line.floorName ?? "") === newFloor &&
        (line.flatName ?? "") === newFlat
      );
    });
    return !duplicate;
  };

  // Handle budget allocation tracking
  const handleBudgetAllocation = (
    budgetLineId: number,
    allocatedAmount: number,
    previousAmount: number = 0,
    previousBudgetLineId?: number
  ) => {
    setBudgetAllocations((prev) => {
      const newAllocations = { ...prev };

      // If changing budget lines during edit, restore allocation to previous budget line
      if (
        previousBudgetLineId &&
        previousBudgetLineId !== budgetLineId &&
        previousAmount > 0
      ) {
        const prevLineAllocation = newAllocations[previousBudgetLineId] || 0;
        newAllocations[previousBudgetLineId] = Math.max(
          0,
          prevLineAllocation - previousAmount
        );
        console.log(
          `Restored ₹${previousAmount} to budget line ${previousBudgetLineId}: ${prevLineAllocation} -> ${newAllocations[previousBudgetLineId]}`
        );
      }

      // Update current budget line allocation
      const currentAllocation = newAllocations[budgetLineId] || 0;
      if (previousBudgetLineId === budgetLineId) {
        // Same budget line - update the difference
        newAllocations[budgetLineId] =
          currentAllocation - previousAmount + allocatedAmount;
      } else {
        // New budget line - add allocation
        newAllocations[budgetLineId] = currentAllocation + allocatedAmount;
      }

      console.log(
        `Budget allocation updated: Line ${budgetLineId}: ${currentAllocation} -> ${newAllocations[budgetLineId]}`
      );
      return newAllocations;
    });
  };

  // Handle budget restoration when edit mode is opened (to show correct available amounts)
  const handleEditModeEntry = (workLineItem: any) => {
    if (workLineItem?.budgetLineId && workLineItem?.allocatedAmount > 0) {
      setBudgetAllocations((prev) => {
        const newAllocations = { ...prev };
        const currentAllocation =
          newAllocations[workLineItem.budgetLineId] || 0;
        // Temporarily restore the allocation to show correct available amounts in dropdown
        newAllocations[workLineItem.budgetLineId] = Math.max(
          0,
          currentAllocation - workLineItem.allocatedAmount
        );
        console.log(`Edit mode: Temporarily restored`, newAllocations);
        return newAllocations;
      });
    }
  };

  // Handle budget restoration when edit is cancelled
  const handleEditModeExit = (
    workLineItem: any,
    wasCancelled: boolean = false
  ) => {
    if (
      wasCancelled &&
      workLineItem?.budgetLineId &&
      workLineItem?.allocatedAmount > 0
    ) {
      setBudgetAllocations((prev) => {
        const newAllocations = { ...prev };
        const currentAllocation =
          newAllocations[workLineItem.budgetLineId] || 0;
        // Restore the allocation since edit was cancelled
        newAllocations[workLineItem.budgetLineId] =
          currentAllocation + workLineItem.allocatedAmount;
        console.log(
          `Edit cancelled: Restored ₹${workLineItem.allocatedAmount} to budget line ${workLineItem.budgetLineId}`
        );
        return newAllocations;
      });
    }
  };

  const submitForm = (data: any) => {
    if (fields.length === 0) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "At least one work order line is required",
      });
      return;
    }

    // Check for validation errors
    if (fields?.find((line: any) => line?.errorFlag)?.errorFlag) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Please fix all errors before proceeding",
      });
      return;
    }

    const payload = {
      ...data,
      workOrderLines: fields,
      step: "workOrderLines",
    };

    console.log("Work Order Lines Payload:", payload);
    props.next(payload);
  };

  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);

  const handleExpand = (expanded: boolean, record: any) => {
    if (expanded) {
      setExpandedRowKeys([record.id]); // expand only one at a time
    } else {
      setExpandedRowKeys([]);
    }
  };

  return (
    <>
      <Block>
        <form className="form-validate" onSubmit={handleSubmit(submitForm)}>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="title">Work BOQ Lines</h6>
            {/* <Button color="primary" onClick={addNewRow} type="button">
              <Icon name="plus" /> Add Work Order Line
            </Button> */}
          </div>

          <Row>
            <div className="table-responsive workBoqTable">
              <div style={{ width: screenSize - 120 + "px", margin: "auto" }}>
                {columns && (
                  <Table
                    className="customTable"
                    columns={columns}
                    dataSource={fields}
                    rowClassName={(record: any) => {
                      return record?.errorFlag == true ? "error-tableRow" : "";
                    }}
                    rowKey="id"
                    scroll={{ x: "max-content" }}
                    expandable={{
                      expandedRowRender: (record: any) => (
                        <div className="expandableBody">
                          <div style={{ flex: 1, textAlign: "left" }}>
                            <div className="mb-3">
                              <strong>Task Description:</strong>
                              <p
                                style={{
                                  margin: 0,
                                  marginTop: 5,
                                  padding: "8px",
                                  backgroundColor: "#f5f5f5",
                                  borderRadius: "4px",
                                }}
                              >
                                {record.taskDescription}
                              </p>
                            </div>

                            <div className="mb-3">
                              {renderDetailedMaterials(
                                record.allocatedMaterials
                              )}
                            </div>

                            {record.uploadedDocs &&
                              record.uploadedDocs.length > 0 && (
                                <div className="mb-3">
                                  <strong>
                                    Uploaded Documents (
                                    {record.uploadedDocs.length}):
                                  </strong>
                                  <div style={{ marginTop: 8 }}>
                                    {record.uploadedDocs.map(
                                      (doc: any, idx: number) => (
                                        <Tag
                                          key={idx}
                                          color="blue"
                                          style={{ margin: "2px" }}
                                          icon={<Icon name="file-docs" />}
                                        >
                                          {doc.name}
                                        </Tag>
                                      )
                                    )}
                                  </div>
                                </div>
                              )}

                            {(record.towerVilla ||
                              record.floorName ||
                              record.flatName) && (
                                <div className="mb-3">
                                  <strong>Location Details:</strong>
                                  <div style={{ marginTop: 5 }}>
                                    {record.towerVilla &&
                                      workLevelWatch !== "Villa" && (
                                        <Tag color="green">
                                          Tower/Block:{" "}
                                          {record.towerVillaName ||
                                            record.towerVilla}
                                        </Tag>
                                      )}
                                    {record.floorName && (
                                      <Tag color="orange">
                                        Floor: {record.floorName}
                                      </Tag>
                                    )}
                                    {record.flatName && (
                                      <Tag color="purple">
                                        {workLevelWatch === "Villa"
                                          ? "Villa"
                                          : "Flat"}
                                        :{" "}
                                        {record.flatNameDisplay ||
                                          record.flatName}
                                      </Tag>
                                    )}
                                    {record.locationId && (
                                      <Tag color="geekblue">
                                        Location: {record.locationName}
                                      </Tag>
                                    )}
                                  </div>
                                </div>
                              )}
                          </div>
                        </div>
                      ),
                      expandedRowKeys,
                      onExpand: handleExpand,
                    }}
                    locale={{
                      emptyText: (
                        <span
                          style={{
                            height: "300px",
                            color: "#333",
                            fontSize: "16px",
                          }}
                        >
                          Please click on "Add New Item" to add Work BOQ lines.
                        </span>
                      ),
                    }}
                    bordered
                    size="small"
                    pagination={false}
                  />
                )}
              </div>

              {/* Summary Section */}
              {fields.length > 0 && (
                <div
                  className="mt-3 p-3 bg-light rounded"
                  style={{
                    maxWidth: "350px",
                    marginLeft: "auto",
                    marginRight: "20px",
                  }}
                >
                  <h6 className="mb-3">Summary</h6>
                  <div className="d-flex justify-content-between mb-2">
                    <span>Total Work Lines:</span>
                    <strong>{fields.length}</strong>
                  </div>
                  <hr className="my-2" />
                  <div className="d-flex justify-content-between">
                    <strong style={{ fontSize: "16px" }}>Gross Total:</strong>
                    <strong style={{ fontSize: "16px", color: "#28a745" }}>
                      ₹
                      {fields
                        .reduce((sum: number, field: WorkOrderLine) => {
                          return (
                            sum + (field.quantity || 0) * (field.rate || 0)
                          );
                        }, 0)
                        .toLocaleString()}
                    </strong>
                  </div>
                </div>
              )}

              <Row style={{ margin: "20px" }}>
                <Col className="justify-between">
                  <a onClick={addNewRow} className="addNewItem">
                    <Icon name="plus-round" />
                    <span>Add New Work Order Line</span>
                  </a>
                  {/* {fields.length > 0 && (
                    <div className="w-40">
                      <div className="d-flex justify-content-between mb-2">
                        <div className="w-50 justify-end">
                          <strong>Total Work Order Lines</strong>
                        </div>
                        <span className="r_align">
                          <Badge color="primary">{totals.totalLines}</Badge>
                        </span>
                      </div>
                      <div className="d-flex justify-content-between mb-2">
                        <div className="w-50 justify-end">
                          <strong>Total Task Quantity</strong>
                        </div>
                        <span className="r_align">
                          <Badge color="info">
                            {totals.totalQuantity.toFixed(2)}
                          </Badge>
                        </span>
                      </div>
                      <div className="d-flex justify-content-between mb-2">
                        <div className="w-50 justify-end">
                          <strong>Total Materials</strong>
                        </div>
                        <span className="r_align">
                          <Badge color="success">{totals.totalMaterials}</Badge>
                        </span>
                      </div>
                      <div className="d-flex justify-content-between">
                        <div className="w-50 justify-end">
                          <strong>Total Material Quantity</strong>
                        </div>
                        <span className="r_align">
                          <Badge color="warning">
                            {totals.totalMaterialQuantity.toFixed(2)}
                          </Badge>
                        </span>
                      </div>
                    </div>
                  )} */}
                </Col>
              </Row>
            </div>
          </Row>

          <div className="actions clearfix">
            <ul>
              <li>
                <Button
                  title={fields.length === 0 ? "Please add work boq lines" : ""}
                  color="primary"
                  type="submit"
                  disabled={
                    fields?.length == 0 ||
                    fields?.find((line: any) => line?.errorFlag)?.errorFlag
                  }
                  onClick={() => {
                    if (fields.length == 0) {
                      Swal.fire({
                        icon: "info",
                        title: "Work BOQ Lines",
                        text: "Please Add Work BOQ Lines",
                      });
                      return;
                    }
                  }}
                >
                  Next
                </Button>
              </li>
              <li>
                <Button
                  color="primary"
                  onClick={props.prev}
                  className="btn-dim btn btn-secondary"
                >
                  Previous
                </Button>
              </li>
            </ul>
          </div>
        </form>
      </Block>

      {activeWorkLine.action && (
        <WorkOrderLineModal
          modal={!!activeWorkLine.action}
          closeModal={() => {
            // Handle budget restoration if edit was cancelled
            if (activeWorkLine.action === "edit") {
              handleEditModeExit(activeWorkLine.item, true);
            }
            setActiveWorkLine({
              action: null,
              index: null,
              item: null,
            });
          }}
          append={(data: any) => {
            if (!validateDuplicateTaskAtHierarchy(data)) {
              Swal.fire({
                icon: "error",
                title: "Duplicate Task at Same Hierarchy",
                text: "This task title already exists at the same site hierarchy. Change the task title or pick a different Tower/Floor/Flat.",
              });
              return false;
            }
            append(data);
            return true;
          }}
          update={(index: any, data: any) => {
            if (!validateDuplicateTaskAtHierarchy(data, index)) {
              Swal.fire({
                icon: "error",
                title: "Duplicate Task at Same Hierarchy",
                text: "This task title already exists at the same site hierarchy. Change the task title or pick a different Tower/Floor/Flat.",
              });
              return false;
            }
            update(index, { ...data, errorFlag: false, errorMessage: [] });
            return true;
          }}
          formData={activeWorkLine}
          workLevel={workLevelWatch}
          materialBoqId={watch("materialBoqId")}
          siteId={siteIdWatch}
          budgetId={watch("budgetId")}
          budgetAllocations={budgetAllocations}
          onBudgetAllocation={handleBudgetAllocation}
          onEditModeExit={handleEditModeExit}
        />
      )}
    </>
  );
};

export default WorkBoqOrderLines;
